const MEXICO_CITY_DATE_FORMATTER = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'America/Mexico_City',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

const MEXICO_CITY_DATE_TIME_FORMATTER = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'America/Mexico_City',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
});

function getDateTimeParts(formatter, date) {
  const parts = formatter.formatToParts(date);
  const values = {};

  for (const part of parts) {
    values[part.type] = part.value;
  }

  return values;
}

function formatMexicoCityTime(value) {
  if (!value) {
    return '';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const parts = getDateTimeParts(MEXICO_CITY_DATE_TIME_FORMATTER, date);

  return `${parts.day || ''}/${parts.month || ''}/${parts.year || ''}, ${parts.hour || ''}:${parts.minute || ''}:${parts.second || ''}`;
}

export function normalizeSearchTerm(value) {
  return String(value || '').trim().toLowerCase();
}

export function compactSearchValue(value) {
  return normalizeSearchTerm(value).replace(/[^a-z0-9]/g, '');
}

export function getDateSearchValues(value) {
  if (!value) {
    return [];
  }

  const rawValue = String(value).trim();
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return rawValue ? [rawValue] : [];
  }

  const mexicoCityDateParts = getDateTimeParts(MEXICO_CITY_DATE_FORMATTER, date);
  const year = mexicoCityDateParts.year || '';
  const month = mexicoCityDateParts.month || '';
  const day = mexicoCityDateParts.day || '';

  const yyyyMmDd = [year, month, day].filter(Boolean).join('-');
  const ddMmYyyy = [day, month, year].filter(Boolean).join('-');
  const mmDdYyyy = [month, day, year].filter(Boolean).join('-');

  return [
    rawValue,
    rawValue.split('T')[0],
    formatMexicoCityTime(value),
    date.toLocaleString(),
    yyyyMmDd,
    yyyyMmDd.replace(/-/g, '/'),
    ddMmYyyy,
    ddMmYyyy.replace(/-/g, '/'),
    mmDdYyyy,
    mmDdYyyy.replace(/-/g, '/'),
  ].filter(Boolean);
}

function appendSearchValue(normalizedParts, compactParts, value) {
  if (value === null || value === undefined || value === '') {
    return;
  }

  const normalizedValue = normalizeSearchTerm(value);

  if (!normalizedValue) {
    return;
  }

  normalizedParts.push(normalizedValue);

  const compactValue = compactSearchValue(normalizedValue);

  if (compactValue) {
    compactParts.push(compactValue);
  }
}

function appendPaymentSearchValues(payments, generalNormalizedParts, generalCompactParts) {
  if (!Array.isArray(payments)) {
    return;
  }

  for (const paymentLink of payments) {
    appendSearchValue(generalNormalizedParts, generalCompactParts, paymentLink?.amount);
    appendSearchValue(generalNormalizedParts, generalCompactParts, paymentLink?.payment?._id);
    appendSearchValue(generalNormalizedParts, generalCompactParts, paymentLink?.payment?.reference);
    appendSearchValue(generalNormalizedParts, generalCompactParts, paymentLink?.payment?.currency);
    appendSearchValue(generalNormalizedParts, generalCompactParts, paymentLink?.payment?.movement?.claveRastreo);
    appendSearchValue(generalNormalizedParts, generalCompactParts, paymentLink?.payment?.movement?.conceptoPago);
  }
}

export function buildOperationSearchIndex(operation = {}) {
  const generalNormalizedParts = [];
  const generalCompactParts = [];
  const ticketNormalizedParts = [];
  const ticketCompactParts = [];

  const appendGeneralValue = value => appendSearchValue(generalNormalizedParts, generalCompactParts, value);
  const appendTicketValue = value => appendSearchValue(ticketNormalizedParts, ticketCompactParts, value);

  appendGeneralValue(operation.type);
  appendGeneralValue(operation.op);
  appendGeneralValue(operation.platform);
  appendGeneralValue(operation.provider);
  appendGeneralValue(operation.customer);
  appendGeneralValue(operation.date);

  for (const dateValue of getDateSearchValues(operation.date)) {
    appendGeneralValue(dateValue);
  }

  appendGeneralValue(operation.reservation_code);
  appendGeneralValue(operation.ticket_number);
  appendGeneralValue(operation.passengers);
  appendGeneralValue(operation.description);
  appendGeneralValue(operation.cost_total);
  appendGeneralValue(operation.sale_price);
  appendGeneralValue(operation.currency);
  appendGeneralValue(operation.invoiceRequired ? 'invoice required' : '');
  appendGeneralValue(operation.credit ? 'credit' : '');
  appendGeneralValue(operation.cost_iva);
  appendGeneralValue(operation.sale_iva);
  appendGeneralValue(operation.sales_agent?.firstName);
  appendGeneralValue(operation.sales_agent?.lastName);
  appendGeneralValue(operation.odoo_so);
  appendGeneralValue(operation.odoo_po);
  appendGeneralValue(operation.transaction);
  appendGeneralValue(operation.pq);
  appendGeneralValue(operation._id);

  appendTicketValue(operation.ticket_number);

  appendPaymentSearchValues(
    operation.payments_from_customer,
    generalNormalizedParts,
    generalCompactParts,
  );
  appendPaymentSearchValues(
    operation.payments_to_provider,
    generalNormalizedParts,
    generalCompactParts,
  );

  return {
    generalText: generalNormalizedParts.join('\n'),
    generalCompactText: generalCompactParts.join('|'),
    ticketText: ticketNormalizedParts.join('\n'),
    ticketCompactText: ticketCompactParts.join('|'),
  };
}

export function createOperationSearchQuery(value) {
  const normalized = normalizeSearchTerm(value);

  if (!normalized) {
    return null;
  }

  const compact = compactSearchValue(normalized);

  return {
    normalized,
    compact,
    preferTicketMatches: compact.length >= 5 && /^\d+$/.test(compact) && !normalized.includes(' '),
  };
}

export function matchesOperationSearchIndex(searchIndex, searchQuery, mode = 'general') {
  if (!searchIndex || !searchQuery?.normalized) {
    return false;
  }

  const normalizedText = mode === 'ticket'
    ? searchIndex.ticketText
    : searchIndex.generalText;
  const compactText = mode === 'ticket'
    ? searchIndex.ticketCompactText
    : searchIndex.generalCompactText;

  if (normalizedText.includes(searchQuery.normalized)) {
    return true;
  }

  return Boolean(searchQuery.compact) && compactText.includes(searchQuery.compact);
}
